import React, { useState, useMemo, useEffect, useRef } from "react";
import { IconX, IconPlus, IconTrash, IconLogout } from "@tabler/icons-react";
import Filters from "components/layout/filters";
import Sorters from "components/layout/sorters";
import { useSharedState } from "state";
import { TaskFilters, TaskFiltersDefault } from "types/task";
import getUserInitials from "helpers/get-user-initials";
import DangerModal from "components/modal/danger";

export default function PageLayout({
  children,
  header,
  subheaderText,
  search,
  filters,
  sorters = true,
  buttonText,
  buttonAction,
  additionalButton,
}: {
  children: React.JSX.Element;
  header?: string | React.JSX.Element;
  subheaderText?: string;
  search: boolean;
  filters: boolean;
  sorters?: boolean;
  buttonText?: string;
  buttonAction?: () => void;
  additionalButton?: React.JSX.Element;
}) {
  const {
    user,
    logout,
    filtersCount,
    currentPageTab,
    resetPageTab,
    searchQuery,
    setSearchQuery,
    filtersList,
    setFiltersList,
    handleSearch,
    handleFilter,
    handleClearFilters,
    sortersList,
  } = useSharedState();

  const [timeoutId, setTimeoutId] = useState<number | null>(null);
  const prevMainPath = useRef("");

  const isTaskPage = useMemo(() => {
    return window.location.pathname === "/tasks";
  }, [window.location.pathname]);

  const isDocumentPage = useMemo(() => {
    return window.location.pathname === "/documents";
  }, [window.location.pathname]);

  const isPaymentPage = useMemo(() => {
    return window.location.pathname === "/payments";
  }, [window.location.pathname]);

  const isLayoutCollapsed = useMemo(() => {
    return isTaskPage || isDocumentPage || isPaymentPage;
  }, [isTaskPage, isDocumentPage, isPaymentPage]);

  useEffect(() => {
    // Get the main route path without sub-routes and query params
    const currentMainPath = window.location.pathname.split("/")[1];
    const currentSearchParams = window.location.search;

    // Create a unique key combining path and location_name (if exists)
    const locationParam = new URLSearchParams(currentSearchParams).get("location_name");
    const currentKey = `${currentMainPath}${locationParam ? `_${locationParam}` : ""}`;

    if (prevMainPath.current !== currentMainPath && prevMainPath.current !== "") {
      // Only clear filters when switching between different main routes
      (searchQuery || filtersCount || sortersList.length) && handleClearFilters();
      currentPageTab !== "cards" && resetPageTab();
    }

    prevMainPath.current = currentMainPath;
  }, [window.location.pathname]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchQuery = event.target.value;
    setSearchQuery(newSearchQuery);

    timeoutId && clearTimeout(timeoutId);
    const newTimeoutId = window.setTimeout(() => {
      handleSearch(newSearchQuery);
    }, 1000);
    setTimeoutId(newTimeoutId);
  };

  const handleFilterChange = () => {
    // Create a new object by spreading TaskFiltersDefault
    const newFilterQuery: TaskFilters = { ...TaskFiltersDefault };
    Object.entries(filtersList).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        newFilterQuery[key as keyof TaskFilters] = value;
      }
    });
    handleFilter(newFilterQuery);
  };

  const headerSearchInput = (
    <div className="flex-grow-1 flex-md-grow-0">
      <div className="input-group input-group-flat focus-within:shadow-none">
        <input
          className="form-control d-inline-block p-4 rounded-tl-2xl rounded-bl-2xl outline-none"
          type="search"
          placeholder="Поиск"
          value={searchQuery}
          onChange={handleSearchChange}
        />
        <span className="input-group-text p-4 rounded-tr-2xl rounded-br-2xl">
          {searchQuery && (
            <div className="link-secondary cursor-pointer" onClick={() => handleSearch("")}>
              <IconX className="icon" />
            </div>
          )}
        </span>
      </div>
    </div>
  );

  const headerButtons = (filters || searchQuery || buttonText) && (
    <div className="d-flex flex-wrap gap-2 w-full md-w-fit">
      {filters && (
        <Filters
          filtersList={filtersList}
          setFiltersList={setFiltersList}
          onFiltersReset={handleClearFilters}
          onFiltersSubmit={handleFilterChange}
        />
      )}
      {sorters && <Sorters />}

      {filtersCount ? (
        <button className="btn btn-ghost-danger grow" onClick={handleClearFilters}>
          <IconTrash className="icon" />
          Очистить всё
        </button>
      ) : undefined}

      {additionalButton}

      {buttonText && (
        <button className={`btn btn-primary grow ${isLayoutCollapsed && "bg-[#004369]"}`.trim()} onClick={buttonAction}>
          <IconPlus className="icon" />
          {buttonText}
        </button>
      )}
    </div>
  );

  return (
    <>
      <div className="page-wrapper lg:flex lg:flex-col lg:gap-6 lg:p-10 p-2">
        {header && (
          <div className="page-header d-print-none !mt-0">
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-semibold">{header}</h3>
                <div className="flex items-center gap-8">
                  <div className="user-info flex items-center gap-3">
                    <div className="user-avatar flex items-center justify-center w-10 h-10 text-sm leading-4 font-medium rounded-full bg-[#FAFAFA]">
                      {getUserInitials(user.result.name) || "-"}
                    </div>
                    <div className="user-name text-sm leading-[17px] font-normal">{user.result.name || "-"}</div>
                  </div>

                  <div>
                    <IconLogout
                      className="text-[#EF4343] cursor-pointer"
                      data-bs-toggle="modal"
                      data-bs-target="#modal-logout"
                    />
                  </div>
                </div>
              </div>

              <div className={`flex items-center ${search ? "justify-between" : "justify-end"} flex-col lg:flex-row`}>
                {search && headerSearchInput}

                {headerButtons}
              </div>
            </div>
          </div>
        )}

        <div className="page-body !m-0">{children}</div>
      </div>

      <DangerModal
        modal_id="modal-logout"
        dangerDescription="Вы действительно хотите выйти из учётной записи?"
        cancel={() => undefined}
        confirm={logout}
      />
    </>
  );
}
